import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, Trash2, RefreshCw, Zap, ArrowRightLeft, ArrowRight, ArrowLeft,
  CheckCircle, AlertCircle, Database, Settings2, CloudDownload, Download
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const APP_FIELDS = [
  { name: 'next_action_owner', label: 'Next Action Owner', type: 'dropdown' },
  { name: 'ai_status', label: 'AI Status', type: 'dropdown' },
  { name: 'manual_followup_required', label: 'Manual Follow-up Required', type: 'checkbox' },
  { name: 'manual_followup_reason', label: 'Manual Follow-up Reason', type: 'dropdown' },
  { name: 'human_takeover', label: 'Human Takeover', type: 'checkbox' },
  { name: 'assigned_agent', label: 'Assigned Agent', type: 'text' },
  { name: 'last_call_outcome', label: 'Last Call Outcome', type: 'dropdown' },
  { name: 'appointment_status', label: 'Appointment Status', type: 'dropdown' },
  { name: 'payment_status', label: 'Payment Status', type: 'dropdown' },
  { name: 'callback_reason', label: 'Callback Reason', type: 'text' },
  { name: 'revenue', label: 'Revenue', type: 'number' },
  { name: 'source', label: 'Lead Source', type: 'text' },
];

const CustomFieldsPage = () => {
  const { user, selectedBusiness } = useAuth();
  const businessId = selectedBusiness?.business_id || user?.business_id;

  const [mappings, setMappings] = useState([]);
  const [ghlFields, setGhlFields] = useState([]);
  const [remoteFields, setRemoteFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedImports, setSelectedImports] = useState({});

  const [newMapping, setNewMapping] = useState({
    app_field_name: '', ghl_field_id: '', ghl_field_name: '',
    field_type: 'text', sync_direction: 'both'
  });
  const [syncResult, setSyncResult] = useState(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 
      'Content-Type': 'application/json', 
      ...(token ? { Authorization: `Bearer ${token}` } : {}) 
    };
  }, []);

  const checkGhlAndFetchFields = useCallback(async () => {
    setSyncing(true);
    try {
      const credRes = await fetch(`${API_URL}/api/ghl/credentials`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (credRes.ok) {
        const credData = await credRes.json();
        setGhlConnected(credData?.is_connected || false);
      }
      const res = await fetch(`${API_URL}/api/ghl/custom-fields`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setGhlFields(data.custom_fields || []);
      }
    } catch (error) {
      console.error('Error fetching GHL fields:', error);
    } finally {
      setSyncing(false);
    }
  }, [authHeaders]);

  const fetchMappings = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/custom-field-mappings`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setMappings(data);
      }
    } catch (error) {
      console.error('Error fetching mappings:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId, authHeaders]);

  useEffect(() => {
    fetchMappings();
    checkGhlAndFetchFields();
  }, [fetchMappings, checkGhlAndFetchFields]);

  const handleCreateMapping = async () => {
    if (!newMapping.app_field_name || !newMapping.ghl_field_id) {
      toast.error('Please select both app field and GHL field');
      return;
    }
    const ghlField = ghlFields.find(f => f.id === newMapping.ghl_field_id);
    try {
      const res = await fetch(`${API_URL}/api/ghl/custom-field-mappings`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ ...newMapping, ghl_field_name: ghlField?.name || newMapping.ghl_field_id }),
      });
      if (res.ok) {
        toast.success('Mapping created');
        setShowAddMapping(false);
        setNewMapping({ app_field_name: '', ghl_field_id: '', ghl_field_name: '', field_type: 'text', sync_direction: 'both' });
        fetchMappings();
      } else { toast.error('Failed to create mapping'); }
    } catch (error) { toast.error('Failed to create mapping'); }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!confirm('Delete this field mapping?')) return;
    try {
      const res = await fetch(`${API_URL}/api/ghl/custom-field-mappings/${mappingId}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) { toast.success('Mapping deleted'); fetchMappings(); }
      else { toast.error('Failed to delete mapping'); }
    } catch (error) { toast.error('Failed to delete mapping'); }
  };

  const fetchRemoteCustomFields = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/ghl/fetch-remote-custom-fields`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const fields = data.custom_fields || [];
        setRemoteFields(fields);
        const initSelected = {};
        fields.forEach(f => { initSelected[f.id] = false; });
        setSelectedImports(initSelected);
        setShowImportModal(true);
        toast.success(`Found ${fields.length} custom field(s) in GHL`);
      } else {
        toast.error('Failed to fetch custom fields from GHL');
      }
    } catch (error) {
      toast.error('Failed to fetch from GHL');
    } finally {
      setSyncing(false);
    }
  };

  const handleImportFields = async () => {
    // For custom fields, "importing" means creating mappings. We'll open the add mapping dialog
    // with the selected field pre-filled
    const selectedFields = remoteFields.filter(f => selectedImports[f.id] && !f.already_mapped);
    if (selectedFields.length === 0) {
      toast.error('No new fields selected');
      return;
    }
    // If only 1, pre-fill the add mapping form
    if (selectedFields.length === 1) {
      const field = selectedFields[0];
      setNewMapping({
        app_field_name: '',
        ghl_field_id: field.id,
        ghl_field_name: field.name,
        field_type: field.dataType || 'text',
        sync_direction: 'both'
      });
      // Also ensure ghlFields includes this field
      if (!ghlFields.find(f => f.id === field.id)) {
        setGhlFields(prev => [...prev, field]);
      }
      setShowImportModal(false);
      setShowAddMapping(true);
      return;
    }
    // For multiple: open add mapping for first, inform user
    toast.info(`${selectedFields.length} fields selected. Creating mapping for first field. Add remaining manually.`);
    const field = selectedFields[0];
    setNewMapping({
      app_field_name: '',
      ghl_field_id: field.id,
      ghl_field_name: field.name,
      field_type: field.dataType || 'text',
      sync_direction: 'both'
    });
    if (!ghlFields.find(f => f.id === field.id)) {
      setGhlFields(prev => [...prev, ...selectedFields]);
    }
    setShowImportModal(false);
    setShowAddMapping(true);
  };

  const getSyncIcon = (direction) => {
    switch (direction) {
      case 'ghl_to_app': return <ArrowLeft className="w-4 h-4" />;
      case 'app_to_ghl': return <ArrowRight className="w-4 h-4" />;
      default: return <ArrowRightLeft className="w-4 h-4" />;
    }
  };

  const handleSyncNow = async (direction = 'both') => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ghl/custom-fields/sync?direction=${direction}`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSyncResult(data);
        if (data.status === 'success') {
          toast.success(`Synced ${data.fields_synced} field(s) across ${data.leads_processed} lead(s)`);
        } else { toast.info(data.reason || 'Sync skipped'); }
      } else { toast.error('Sync failed'); }
    } catch (error) { toast.error('Failed to sync custom fields'); }
    finally { setSyncing(false); }
  };

  const toggleImportSelection = (fieldId) => {
    setSelectedImports(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };

  return (
    <div className="space-y-6" data-testid="custom-fields-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Custom Field Mappings</h1>
          <p className="text-slate-500">Map app fields to GHL custom fields for bidirectional sync</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {ghlConnected && (
            <Button variant="outline" onClick={fetchRemoteCustomFields} disabled={syncing} data-testid="import-remote-fields-btn">
              <CloudDownload className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Import from GHL
            </Button>
          )}
          <Button variant="outline" onClick={checkGhlAndFetchFields} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => handleSyncNow('both')} disabled={syncing || mappings.length === 0} data-testid="sync-custom-fields-btn">
            <Zap className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync Now
          </Button>
          <Button onClick={() => setShowAddMapping(true)} disabled={!ghlConnected} data-testid="add-mapping-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Mapping
          </Button>
        </div>
      </div>

      {/* GHL Status */}
      {!ghlConnected && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            GHL is not connected. Connect GHL in Settings to map custom fields.
          </AlertDescription>
        </Alert>
      )}

      {/* Sync Result */}
      {syncResult && (
        <Alert className={syncResult.status === 'success' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'} data-testid="sync-result">
          {syncResult.status === 'success' ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
          <AlertDescription className={syncResult.status === 'success' ? 'text-green-800' : 'text-amber-800'}>
            {syncResult.status === 'success'
              ? `Synced ${syncResult.fields_synced} field(s) across ${syncResult.leads_processed} lead(s).${syncResult.errors?.length > 0 ? ` ${syncResult.errors.length} error(s).` : ''}`
              : syncResult.reason || 'Sync skipped'}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Mappings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" />
              Active Mappings
            </CardTitle>
            <CardDescription>Fields that sync between Beluga Pro and GHL</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : mappings.length === 0 ? (
              <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                <Database className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                <p>No field mappings configured</p>
                <p className="text-sm mt-1">Add mappings to sync data with GHL</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mappings.map((mapping) => (
                  <div key={mapping.mapping_id}
                    className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg"
                    data-testid={`mapping-item-${mapping.mapping_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        {getSyncIcon(mapping.sync_direction)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">
                            {APP_FIELDS.find(f => f.name === mapping.app_field_name)?.label || mapping.app_field_name}
                          </span>
                          <span className="text-slate-400">&#x2194;</span>
                          <span className="text-orange-600 flex items-center gap-1">
                            <Zap className="w-3 h-3" />{mapping.ghl_field_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-[10px]">{mapping.field_type}</Badge>
                          <Badge className={`text-[10px] ${
                            mapping.sync_direction === 'both' ? 'bg-green-100 text-green-700' :
                            mapping.sync_direction === 'ghl_to_app' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {mapping.sync_direction === 'both' ? 'Bidirectional' :
                             mapping.sync_direction === 'ghl_to_app' ? 'GHL to App' : 'App to GHL'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm"
                      onClick={() => handleDeleteMapping(mapping.mapping_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-mapping-${mapping.mapping_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* GHL Custom Fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              GHL Custom Fields
            </CardTitle>
            <CardDescription>Custom fields available in your GHL location</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {syncing ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : ghlFields.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Zap className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                  <p>No custom fields found in GHL</p>
                  <p className="text-sm mt-1">Create custom fields in GHL first</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {ghlFields.map((field) => {
                    const isMapped = mappings.some(m => m.ghl_field_id === field.id);
                    return (
                      <div key={field.id}
                        className={`p-3 rounded-lg border ${isMapped ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{field.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-[10px]">{field.dataType || 'text'}</Badge>
                              <span className="text-xs text-slate-400 font-mono">{field.id}</span>
                            </div>
                          </div>
                          {isMapped && <CheckCircle className="w-4 h-4 text-green-500" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Import from GHL Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudDownload className="w-5 h-5 text-orange-500" />
              Import Custom Fields from GHL
            </DialogTitle>
            <DialogDescription>Select GHL custom fields to create mappings for</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px] py-4">
            {remoteFields.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>No custom fields found in GHL</p>
              </div>
            ) : (
              <div className="space-y-3">
                {remoteFields.map((field) => (
                  <div key={field.id}
                    className={`p-4 border rounded-lg transition-colors ${
                      field.already_mapped
                        ? 'bg-green-50 border-green-200 opacity-70'
                        : selectedImports[field.id]
                          ? 'bg-cyan-50 border-cyan-300'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`remote-field-${field.id}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={field.already_mapped || selectedImports[field.id] || false}
                        onCheckedChange={() => toggleImportSelection(field.id)}
                        disabled={field.already_mapped}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{field.name}</p>
                          {field.already_mapped && (
                            <Badge className="text-[10px] bg-green-100 text-green-700">Already Mapped</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-[10px]">{field.dataType || 'text'}</Badge>
                          <span className="text-xs text-slate-400 font-mono">{field.id}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button onClick={handleImportFields}
              disabled={importing || Object.values(selectedImports).every(v => !v)}
              data-testid="confirm-import-fields-btn">
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Create Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Mapping Dialog */}
      <Dialog open={showAddMapping} onOpenChange={setShowAddMapping}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Field Mapping</DialogTitle>
            <DialogDescription>Map a Beluga Pro field to a GHL custom field</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>App Field</Label>
              <Select value={newMapping.app_field_name}
                onValueChange={(val) => {
                  const field = APP_FIELDS.find(f => f.name === val);
                  setNewMapping(prev => ({ ...prev, app_field_name: val, field_type: field?.type || 'text' }));
                }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select app field" /></SelectTrigger>
                <SelectContent>
                  {APP_FIELDS.map((field) => (
                    <SelectItem key={field.name} value={field.name}>{field.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GHL Custom Field</Label>
              <Select value={newMapping.ghl_field_id}
                onValueChange={(val) => {
                  const field = ghlFields.find(f => f.id === val);
                  setNewMapping(prev => ({ ...prev, ghl_field_id: val, ghl_field_name: field?.name || '' }));
                }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select GHL field" /></SelectTrigger>
                <SelectContent>
                  {ghlFields.map((field) => (
                    <SelectItem key={field.id} value={field.id}>{field.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sync Direction</Label>
              <Select value={newMapping.sync_direction}
                onValueChange={(val) => setNewMapping(prev => ({ ...prev, sync_direction: val }))}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Bidirectional (both ways)</SelectItem>
                  <SelectItem value="ghl_to_app">GHL to App only</SelectItem>
                  <SelectItem value="app_to_ghl">App to GHL only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMapping(false)}>Cancel</Button>
            <Button onClick={handleCreateMapping} data-testid="create-mapping-submit-btn">Create Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomFieldsPage;
